import { html, nothing, type TemplateResult } from "lit";
import { ref } from "lit/directives/ref.js";
import { repeat } from "lit/directives/repeat.js";
import type { PlanChecklistStep } from "../components/plan-checklist.ts";
import type { SessionsListResult } from "../types.ts";
import type { ChatItem, MessageGroup } from "../types/chat-types.ts";
import type { ChatAttachment, ChatQueueItem } from "../ui-types.ts";
import "../components/file-preview-text.js";
import {
  renderMessageGroup,
  renderReadingIndicatorGroup,
  renderStreamingGroup,
} from "../chat/grouped-render.ts";
import { normalizeMessage, normalizeRoleForGrouping } from "../chat/message-normalizer.ts";
import { icons } from "../icons.ts";
import { renderMarkdownSidebar } from "./markdown-sidebar.ts";
import "../components/plan-checklist.ts";
import "../components/resizable-divider.ts";

export type CompactionIndicatorStatus = {
  active: boolean;
  startedAt: number | null;
  completedAt: number | null;
};

export type ChatProps = {
  sessionKey: string;
  onSessionKeyChange: (next: string) => void;
  thinkingLevel: string | null;
  showThinking: boolean;
  loading: boolean;
  sending: boolean;
  canAbort?: boolean;
  compactionStatus?: CompactionIndicatorStatus | null;
  messages: unknown[];
  toolMessages: unknown[];
  stream: string | null;
  streamStartedAt: number | null;
  assistantAvatarUrl?: string | null;
  draft: string;
  queue: ChatQueueItem[];
  connected: boolean;
  canSend: boolean;
  disabledReason: string | null;
  error: string | null;
  sessions: SessionsListResult | null;
  // Focus mode
  focusMode: boolean;
  // Sidebar state
  sidebarOpen?: boolean;
  sidebarContent?: string | null;
  sidebarError?: string | null;
  splitRatio?: number;
  assistantName: string;
  assistantAvatar: string | null;
  // Image attachments
  attachments?: ChatAttachment[];
  onAttachmentsChange?: (attachments: ChatAttachment[]) => void;
  // Scroll control
  showNewMessages?: boolean;
  onScrollToBottom?: () => void;
  // Event handlers
  onRefresh: () => void;
  onToggleFocusMode: () => void;
  onDraftChange: (next: string) => void;
  onSend: () => void;
  onAbort?: () => void;
  onQueueRemove: (id: string) => void;
  onNewSession: () => void;
  onResetSession?: () => void;
  onOpenSidebar?: (content: string) => void;
  onCloseSidebar?: () => void;
  onSplitRatioChange?: (ratio: number) => void;
  onChatScroll?: (event: Event) => void;
  // Slash-command autocomplete
  commands?: { name: string; description: string; skillName: string }[];
  cmdMenuOpen?: boolean;
  cmdIndex?: number;
  onCmdMenuOpen?: (filter: string) => void;
  onCmdMenuClose?: () => void;
  onCmdNav?: (delta: number) => void;
  onCmdSelect?: (name: string) => void;
  // Console bar (skill buttons below chat input)
  consoleVisible: boolean;
  consoleSkills: Array<{ name: string; label: string }>;
  onToggleConsole: () => void;
  onConsoleSkillClick: (skillName: string) => void;
  // File preview
  previewFilePath: string | null;
  previewFileName: string | null;
  previewFileType: "pdf" | "image" | "markdown" | "text" | "other" | null;
  onOpenFilePreview: (filePath: string) => void;
  onOpenFileFolder: (filePath: string) => void;
  onClosePreview: () => void;
  token: string;
};

const COMPACTION_TOAST_DURATION_MS = 5000;

function adjustTextareaHeight(el: HTMLTextAreaElement) {
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
}

function renderCompactionIndicator(status: CompactionIndicatorStatus | null | undefined) {
  if (!status) {
    return nothing;
  }

  // Show "compacting..." while active
  if (status.active) {
    return html`
      <div class="compaction-indicator compaction-indicator--active" role="status" aria-live="polite">
        ${icons.loader} Compacting context...
      </div>
    `;
  }

  // Show "compaction complete" briefly after completion
  if (status.completedAt) {
    const elapsed = Date.now() - status.completedAt;
    if (elapsed < COMPACTION_TOAST_DURATION_MS) {
      return html`
        <div class="compaction-indicator compaction-indicator--complete" role="status" aria-live="polite">
          ${icons.check} Context compacted
        </div>
      `;
    }
  }

  return nothing;
}

function generateAttachmentId(): string {
  return `att-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Max file size: 50 MB */
const MAX_FILE_SIZE = 50 * 1024 * 1024;

/** Accepted document MIME types */
const DOCUMENT_MIMES = new Set([
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "text/plain",
  "text/csv",
  "text/html",
  "text/markdown",
  "application/json",
  "application/xml",
  "text/xml",
  "text/yaml",
  "application/x-yaml",
]);

/** Document file extensions for the file picker accept attribute */
const DOCUMENT_ACCEPT =
  "image/*,.pdf,.docx,.doc,.pptx,.ppt,.xlsx,.xls,.txt,.csv,.json,.md,.html,.htm,.xml,.yaml,.yml,.tex,.latex";

function isDocumentMime(mime: string): boolean {
  return DOCUMENT_MIMES.has(mime);
}

function isImageMime(mime: string): boolean {
  return mime.startsWith("image/");
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fileExtIcon(fileName: string): string {
  const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
  switch (ext) {
    case "pdf":
      return "PDF";
    case "doc":
    case "docx":
      return "DOC";
    case "ppt":
    case "pptx":
      return "PPT";
    case "xls":
    case "xlsx":
      return "XLS";
    case "txt":
      return "TXT";
    case "csv":
      return "CSV";
    case "json":
      return "JSON";
    case "md":
      return "MD";
    case "html":
    case "htm":
      return "HTML";
    case "xml":
      return "XML";
    case "yaml":
    case "yml":
      return "YAML";
    case "tex":
    case "latex":
      return "TEX";
    default:
      return "FILE";
  }
}

/**
 * Process a File object into a ChatAttachment.
 * Returns null if the file is too large or has an unsupported type.
 */
function processFileToAttachment(
  file: File,
  onError?: (msg: string) => void,
): Promise<ChatAttachment | null> {
  return new Promise((resolve) => {
    if (file.size > MAX_FILE_SIZE) {
      onError?.(`File "${file.name}" is too large (${formatFileSize(file.size)}). Max: 50 MB.`);
      resolve(null);
      return;
    }

    const mime = file.type || "application/octet-stream";
    const isImage = isImageMime(mime);
    const isDoc = isDocumentMime(mime);

    if (!isImage && !isDoc) {
      // Try extension-based detection for files without proper MIME
      const ext = file.name.split(".").pop()?.toLowerCase() ?? "";
      const extMimes: Record<string, string> = {
        pdf: "application/pdf",
        docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        doc: "application/msword",
        pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
        ppt: "application/vnd.ms-powerpoint",
        xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        xls: "application/vnd.ms-excel",
        txt: "text/plain",
        csv: "text/csv",
        json: "application/json",
        md: "text/markdown",
        html: "text/html",
        htm: "text/html",
        xml: "application/xml",
        yaml: "text/yaml",
        yml: "text/yaml",
        tex: "text/plain",
        latex: "text/plain",
      };
      const fallbackMime = extMimes[ext];
      if (!fallbackMime) {
        onError?.(`Unsupported file type: "${file.name}" (${mime})`);
        resolve(null);
        return;
      }
      // Re-process with corrected MIME
      const correctedFile = new File([file], file.name, { type: fallbackMime });
      processFileToAttachment(correctedFile, onError).then(resolve);
      return;
    }

    // For documents, keep the raw File object and skip readAsDataURL entirely.
    // Documents will be uploaded via HTTP POST to /api/upload, avoiding the
    // 50MB base64-over-WebSocket bottleneck that caused 1006 disconnections.
    if (!isImage) {
      resolve({
        id: generateAttachmentId(),
        dataUrl: "", // Not needed — documents upload via HTTP
        mimeType: mime,
        kind: "document",
        fileName: file.name,
        fileSize: file.size,
        file, // Raw File object for HTTP upload
      });
      return;
    }

    // Images still use dataURL for inline preview
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      const dataUrl = reader.result as string;
      resolve({
        id: generateAttachmentId(),
        dataUrl,
        mimeType: mime,
        kind: "image",
        fileName: file.name,
        fileSize: file.size,
      });
    });
    reader.addEventListener("error", () => {
      onError?.(`Failed to read file: "${file.name}"`);
      resolve(null);
    });
    reader.readAsDataURL(file);
  });
}

/** Add files (from picker, drop, or paste) to the attachments list. */
async function addFiles(files: FileList | File[], props: ChatProps) {
  if (!props.onAttachmentsChange) {
    return;
  }
  const current = props.attachments ?? [];
  const newAttachments: ChatAttachment[] = [];

  for (const file of Array.from(files)) {
    const att = await processFileToAttachment(file);
    if (att) {
      newAttachments.push(att);
    }
  }

  if (newAttachments.length > 0) {
    props.onAttachmentsChange([...current, ...newAttachments]);
  }
}

function handlePaste(e: ClipboardEvent, props: ChatProps) {
  const items = e.clipboardData?.items;
  if (!items || !props.onAttachmentsChange) {
    return;
  }

  const fileItems: DataTransferItem[] = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.kind === "file") {
      fileItems.push(item);
    }
  }

  if (fileItems.length === 0) {
    return;
  }

  e.preventDefault();

  const files: File[] = [];
  for (const item of fileItems) {
    const file = item.getAsFile();
    if (file) {
      files.push(file);
    }
  }

  if (files.length > 0) {
    void addFiles(files, props);
  }
}

function renderAttachmentPreview(props: ChatProps) {
  const attachments = props.attachments ?? [];
  if (attachments.length === 0) {
    return nothing;
  }

  const removeAtt = (id: string) => {
    const next = (props.attachments ?? []).filter((a) => a.id !== id);
    props.onAttachmentsChange?.(next);
  };

  return html`
    <div class="chat-attachments">
      ${attachments.map((att) => {
        if (att.kind === "document") {
          const label = att.fileName ?? "file";
          const ext = fileExtIcon(label);
          const size = att.fileSize ? formatFileSize(att.fileSize) : "";
          return html`
            <div class="chat-attachment chat-attachment--doc">
              <div class="chat-attachment__doc-icon">${ext}</div>
              <div class="chat-attachment__doc-info">
                <div class="chat-attachment__doc-name" title=${label}>${label}</div>
                ${size ? html`<div class="chat-attachment__doc-size">${size}</div>` : nothing}
              </div>
              <button
                class="chat-attachment__remove"
                type="button"
                aria-label="Remove attachment"
                @click=${() => removeAtt(att.id)}
              >${icons.x}</button>
            </div>
          `;
        }
        // Image attachment
        return html`
          <div class="chat-attachment">
            <img
              src=${att.dataUrl}
              alt="Attachment preview"
              class="chat-attachment__img"
            />
            <button
              class="chat-attachment__remove"
              type="button"
              aria-label="Remove attachment"
              @click=${() => removeAtt(att.id)}
            >${icons.x}</button>
          </div>
        `;
      })}
    </div>
  `;
}

/** Compute the slash-command filter from a draft string.
 * Returns non-null string when draft is a single leading-slash token (no space yet). */
function resolveCmdFilter(draft: string): string | null {
  const m = /^\/(\S*)$/.exec(draft);
  return m ? m[1] : null;
}

function renderCmdMenu(props: ChatProps) {
  const filter = resolveCmdFilter(props.draft);
  if (!props.cmdMenuOpen || filter === null || !props.commands?.length) {
    return nothing;
  }
  const lower = filter.toLowerCase();
  const matches = props.commands.filter(
    (c) => !lower || c.name.toLowerCase().startsWith(lower) || c.name.toLowerCase().includes(lower),
  );
  if (!matches.length) {
    return nothing;
  }
  const activeIdx = props.cmdIndex ?? 0;
  return html`
    <style>
      .cmd-menu {
        position: absolute;
        bottom: 100%;
        left: 0;
        right: 0;
        margin-bottom: 4px;
        background: var(--panel);
        border: 1px solid var(--border-strong, var(--border));
        border-radius: 8px;
        box-shadow: var(--shadow-md);
        max-height: 240px;
        overflow-y: auto;
        z-index: 200;
      }
      .cmd-menu__item {
        display: flex;
        align-items: baseline;
        gap: 8px;
        padding: 8px 12px;
        cursor: pointer;
        border-radius: 6px;
        margin: 2px;
      }
      .cmd-menu__item:hover,
      .cmd-menu__item--active {
        background: var(--panel-hover);
      }
      .cmd-menu__name {
        font-family: var(--font-mono, monospace);
        font-size: 13px;
        color: var(--accent);
        white-space: nowrap;
        flex-shrink: 0;
      }
      .cmd-menu__desc {
        font-size: 12px;
        color: var(--text-muted, var(--text));
        opacity: 0.65;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        min-width: 0;
      }
    </style>
    <div class="cmd-menu" role="listbox" aria-label="Slash commands">
      ${matches.map(
        (cmd, idx) => html`
          <div
            class="cmd-menu__item ${idx === activeIdx ? "cmd-menu__item--active" : ""}"
            role="option"
            aria-selected=${idx === activeIdx}
            @mousedown=${(e: Event) => {
              e.preventDefault(); // Don't blur the textarea
              props.onCmdSelect?.(cmd.name);
            }}
          >
            <span class="cmd-menu__name">/${cmd.name}</span>
            <span class="cmd-menu__desc">${cmd.description}</span>
          </div>
        `,
      )}
    </div>
  `;
}

function resolvePreviewUrl(filePath: string, token: string): string {
  return `/api/files/preview?path=${encodeURIComponent(filePath)}&token=${encodeURIComponent(token)}`;
}

function renderFilePreview(props: ChatProps): TemplateResult | typeof nothing {
  const fileName = props.previewFileName ?? "File";
  const fileType = props.previewFileType ?? "other";
  const token = (props as unknown as { token?: string }).token ?? "";
  const previewUrl = props.previewFilePath ? resolvePreviewUrl(props.previewFilePath, token) : "";

  return html`
    <div class="sidebar-panel chat-preview">
      <div class="sidebar-header">
        <div class="sidebar-title">${fileName}</div>
        <div style="display:flex;gap:4px;align-items:center;">
          <a
            href=${previewUrl}
            download=${fileName}
            class="btn btn--sm"
            title="Download file"
          >Download</a>
          <button
            class="btn btn--sm"
            @click=${props.onClosePreview}
            title="Close preview"
          >X</button>
        </div>
      </div>
      <div class="sidebar-content chat-preview__content">
        ${fileType === "pdf"
          ? html`<embed src=${previewUrl} type="application/pdf" class="chat-preview__embed" />`
          : nothing}
        ${fileType === "image"
          ? html`<img src=${previewUrl} class="chat-preview__image" alt=${fileName} />`
          : nothing}
        ${fileType === "markdown" || fileType === "text"
          ? html`<file-preview-text
              .url=${previewUrl}
              .fileType=${fileType}
              .fileName=${fileName}
            ></file-preview-text>`
          : nothing}
        ${fileType === "other"
          ? html`<div class="chat-preview__download">
              <p>Preview not available for this file type.</p>
              <a href=${previewUrl} download=${fileName} class="btn primary">Download ${fileName}</a>
            </div>`
          : nothing}
      </div>
    </div>
  `;
}

export function renderChat(props: ChatProps) {
  const canCompose = props.connected;
  const isBusy = props.sending || props.stream !== null;
  const canAbort = Boolean(props.canAbort && props.onAbort);
  const activeSession = props.sessions?.sessions?.find((row) => row.key === props.sessionKey);
  const reasoningLevel = activeSession?.reasoningLevel ?? "off";
  const showReasoning = props.showThinking && reasoningLevel !== "off";
  const assistantIdentity = {
    name: props.assistantName,
    avatar: props.assistantAvatar ?? props.assistantAvatarUrl ?? null,
  };

  const hasAttachments = (props.attachments?.length ?? 0) > 0;
  const composePlaceholder = props.connected
    ? hasAttachments
      ? "Add a message or paste/drop more files..."
      : "Message (↩ to send, Shift+↩ for line breaks, paste images, attach files)"
    : "Connect to the gateway to start chatting…";

  const splitRatio = props.splitRatio ?? 0.6;
  const sidebarOpen = Boolean((props.sidebarOpen && props.onCloseSidebar) || props.previewFilePath);
  const previewMode = Boolean(props.previewFilePath);
  const activePlan = deriveActivePlan(props);
  const thread = html`
    <div
      class="chat-thread"
      role="log"
      aria-live="polite"
      @scroll=${props.onChatScroll}
    >
      ${
        props.loading
          ? html`
              <div class="muted">Loading chat…</div>
            `
          : nothing
      }
      ${repeat(
        buildChatItems(props),
        (item) => item.key,
        (item) => {
          if (item.kind === "divider") {
            return html`
              <div class="chat-divider" role="separator" data-ts=${String(item.timestamp)}>
                <span class="chat-divider__line"></span>
                <span class="chat-divider__label">${item.label}</span>
                <span class="chat-divider__line"></span>
              </div>
            `;
          }

          if (item.kind === "reading-indicator") {
            return renderReadingIndicatorGroup(assistantIdentity);
          }

          if (item.kind === "stream") {
            return renderStreamingGroup(
              item.text,
              item.startedAt,
              props.onOpenSidebar,
              assistantIdentity,
            );
          }

          if (item.kind === "group") {
            return renderMessageGroup(item, {
              onOpenSidebar: props.onOpenSidebar,
              showReasoning,
              assistantName: props.assistantName,
              assistantAvatar: assistantIdentity.avatar,
              toolCardActions: {
                onOpenFilePreview: props.onOpenFilePreview,
                onOpenFileFolder: props.onOpenFileFolder,
              },
            });
          }

          return nothing;
        },
      )}
    </div>
  `;

  return html`
    <section class="card chat">
      ${props.disabledReason ? html`<div class="callout">${props.disabledReason}</div>` : nothing}

      ${props.error ? html`<div class="callout danger">${props.error}</div>` : nothing}

      ${
        props.focusMode
          ? html`
            <button
              class="chat-focus-exit"
              type="button"
              @click=${props.onToggleFocusMode}
              aria-label="Exit focus mode"
              title="Exit focus mode"
            >
              ${icons.x}
            </button>
          `
          : nothing
      }

      <div
        class="chat-split-container ${sidebarOpen ? "chat-split-container--open" : ""}"
      >
        <div
          class="chat-main"
          style="flex: ${sidebarOpen ? `0 0 ${splitRatio * 100}%` : "1 1 100%"}"
        >
          ${thread}
        </div>

        ${
          sidebarOpen
            ? html`
              <resizable-divider
                .splitRatio=${splitRatio}
                @resize=${(e: CustomEvent) => props.onSplitRatioChange?.(e.detail.splitRatio)}
              ></resizable-divider>
              <div class="chat-sidebar ${previewMode ? "chat-sidebar--preview" : ""}">
                ${previewMode
                  ? renderFilePreview(props)
                  : renderMarkdownSidebar({
                      content: props.sidebarContent ?? null,
                      error: props.sidebarError ?? null,
                      onClose: props.onCloseSidebar!,
                      onViewRawText: () => {
                        if (!props.sidebarContent || !props.onOpenSidebar) {
                          return;
                        }
                        props.onOpenSidebar(`\`\`\`\n${props.sidebarContent}\n\`\`\``);
                      },
                    })}
              </div>
            `
            : nothing
        }
      </div>

      ${
        activePlan
          ? html`<ig-plan-checklist
              style="flex-shrink:0;"
              .planTitle=${activePlan.title ?? ""}
              .steps=${activePlan.steps}
            ></ig-plan-checklist>`
          : nothing
      }

      ${
        props.queue.length
          ? html`
            <div class="chat-queue" role="status" aria-live="polite">
              <div class="chat-queue__title">Queued (${props.queue.length})</div>
              <div class="chat-queue__list">
                ${props.queue.map(
                  (item) => html`
                    <div class="chat-queue__item">
                      <div class="chat-queue__text">
                        ${
                          item.text ||
                          (item.attachments?.length
                            ? (() => {
                                const imgs = item.attachments!.filter(
                                  (a) => a.kind === "image",
                                ).length;
                                const docs = item.attachments!.filter(
                                  (a) => a.kind === "document",
                                ).length;
                                const parts: string[] = [];
                                if (imgs) {
                                  parts.push(`${imgs} image${imgs > 1 ? "s" : ""}`);
                                }
                                if (docs) {
                                  parts.push(`${docs} file${docs > 1 ? "s" : ""}`);
                                }
                                return (
                                  parts.join(", ") || `${item.attachments!.length} attachment(s)`
                                );
                              })()
                            : "")
                        }
                      </div>
                      <button
                        class="btn chat-queue__remove"
                        type="button"
                        aria-label="Remove queued message"
                        @click=${() => props.onQueueRemove(item.id)}
                      >
                        ${icons.x}
                      </button>
                    </div>
                  `,
                )}
              </div>
            </div>
          `
          : nothing
      }

      ${renderCompactionIndicator(props.compactionStatus)}

      ${
        props.showNewMessages
          ? html`
            <button
              class="btn chat-new-messages"
              type="button"
              @click=${props.onScrollToBottom}
            >
              New messages ${icons.arrowDown}
            </button>
          `
          : nothing
      }

      <div
        class="chat-compose"
        @dragover=${(e: DragEvent) => {
          e.preventDefault();
          e.stopPropagation();
          const el = (e.currentTarget as HTMLElement).querySelector(".chat-drop-zone");
          if (el) {
            (el as HTMLElement).classList.add("chat-drop-zone--active");
          }
        }}
        @dragleave=${(e: DragEvent) => {
          e.preventDefault();
          const el = (e.currentTarget as HTMLElement).querySelector(".chat-drop-zone");
          // Only deactivate when leaving the compose area itself
          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
          if (
            e.clientX < rect.left ||
            e.clientX > rect.right ||
            e.clientY < rect.top ||
            e.clientY > rect.bottom
          ) {
            if (el) {
              (el as HTMLElement).classList.remove("chat-drop-zone--active");
            }
          }
        }}
        @drop=${(e: DragEvent) => {
          e.preventDefault();
          e.stopPropagation();
          const el = (e.currentTarget as HTMLElement).querySelector(".chat-drop-zone");
          if (el) {
            (el as HTMLElement).classList.remove("chat-drop-zone--active");
          }
          if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
            void addFiles(e.dataTransfer.files, props);
          }
        }}
      >
        <div class="chat-drop-zone">
          <div class="chat-drop-zone__label">${icons.paperclip} Drop files here</div>
        </div>
        ${renderAttachmentPreview(props)}
        ${renderCmdMenu(props)}
        <div class="chat-compose__row">
          <input
            type="file"
            multiple
            accept=${DOCUMENT_ACCEPT}
            style="display:none"
            ${ref((el) => {
              if (el) {
                (el as HTMLInputElement).dataset.chatFileInput = "1";
              }
            })}
            @change=${(e: Event) => {
              const input = e.target as HTMLInputElement;
              if (input.files && input.files.length > 0) {
                void addFiles(input.files, props);
              }
              input.value = "";
            }}
          />
          <button
            class="btn chat-compose__attach"
            type="button"
            title="Attach files"
            ?disabled=${!props.connected}
            @click=${(e: Event) => {
              const compose = (e.target as HTMLElement).closest(".chat-compose");
              const input = compose?.querySelector<HTMLInputElement>("input[data-chat-file-input]");
              input?.click();
            }}
          >${icons.paperclip}</button>
          <label class="field chat-compose__field">
            <span>Message</span>
            <textarea
              ${ref((el) => el && adjustTextareaHeight(el as HTMLTextAreaElement))}
              .value=${props.draft}
              ?disabled=${!props.connected}
              @keydown=${(e: KeyboardEvent) => {
                // Slash-command menu intercepts: ArrowDown/Up/Enter/Tab/Escape when open
                if (props.cmdMenuOpen) {
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    props.onCmdNav?.(1);
                    return;
                  }
                  if (e.key === "ArrowUp") {
                    e.preventDefault();
                    props.onCmdNav?.(-1);
                    return;
                  }
                  if (e.key === "Enter" || e.key === "Tab") {
                    e.preventDefault();
                    const filter = resolveCmdFilter(props.draft) ?? "";
                    const lower = filter.toLowerCase();
                    const matches = (props.commands ?? []).filter(
                      (c) =>
                        !lower ||
                        c.name.toLowerCase().startsWith(lower) ||
                        c.name.toLowerCase().includes(lower),
                    );
                    const selected = matches[props.cmdIndex ?? 0];
                    if (selected) {
                      props.onCmdSelect?.(selected.name);
                    }
                    return;
                  }
                  if (e.key === "Escape") {
                    e.preventDefault();
                    props.onCmdMenuClose?.();
                    return;
                  }
                }
                // Default Enter: send
                if (e.key !== "Enter") {
                  return;
                }
                if (e.isComposing || e.keyCode === 229) {
                  return;
                }
                if (e.shiftKey) {
                  return;
                } // Allow Shift+Enter for line breaks
                if (!props.connected) {
                  return;
                }
                e.preventDefault();
                if (canCompose) {
                  props.onSend();
                }
              }}
              @input=${(e: Event) => {
                const target = e.target as HTMLTextAreaElement;
                adjustTextareaHeight(target);
                const value = target.value;
                props.onDraftChange(value);
                // Open/close command menu based on draft content
                const filter = resolveCmdFilter(value);
                if (filter !== null) {
                  props.onCmdMenuOpen?.(filter);
                } else {
                  props.onCmdMenuClose?.();
                }
              }}
              @paste=${(e: ClipboardEvent) => handlePaste(e, props)}
              placeholder=${composePlaceholder}
            ></textarea>
          </label>
          <div class="chat-compose__actions">
            ${
              canAbort
                ? html`<button
              class="btn"
              ?disabled=${!props.connected}
              @click=${props.onAbort}
            >Stop</button>`
                : nothing
            }
            ${
              !canAbort && props.onResetSession
                ? html`<button
              class="btn"
              ?disabled=${!props.connected || props.sending}
              @click=${props.onResetSession}
              title="Reset/compact this session's context"
            >Reset</button>`
                : nothing
            }
            <button
              class="btn primary"
              ?disabled=${!props.connected}
              @click=${props.onSend}
            >
              ${isBusy ? "Queue" : "Send"}<kbd class="btn-kbd">↵</kbd>
            </button>
          </div>
        </div>
      </div>

      <!-- Console bar (skill buttons below chat input) -->
      <div class="chat-console">
        <button
          class="chat-console__toggle"
          @click=${props.onToggleConsole}
          title=${props.consoleVisible ? "Hide console" : "Show console"}
        >
          ${props.consoleVisible ? "▾" : "▸"} Console
        </button>
        ${props.consoleVisible
          ? html`
              <div class="chat-console__skills">
                ${props.consoleSkills.length === 0
                  ? html`<span class="chat-console__empty muted">No skills pinned to console. Enable "Show in Console" in Skills Management.</span>`
                  : props.consoleSkills.map(
                      (skill) => html`
                        <button
                          class="btn btn--sm chat-console__skill"
                          ?disabled=${!props.connected}
                          @click=${() => props.onConsoleSkillClick(skill.name)}
                          title=${`Inject /${skill.name}`}
                        >${skill.label}</button>
                      `,
                    )}
              </div>
            `
          : nothing}
      </div>
    </section>
  `;
}

const CHAT_HISTORY_RENDER_LIMIT = 200;

function groupMessages(items: ChatItem[]): Array<ChatItem | MessageGroup> {
  const result: Array<ChatItem | MessageGroup> = [];
  let currentGroup: MessageGroup | null = null;

  for (const item of items) {
    if (item.kind !== "message") {
      if (currentGroup) {
        result.push(currentGroup);
        currentGroup = null;
      }
      result.push(item);
      continue;
    }

    const normalized = normalizeMessage(item.message);
    const role = normalizeRoleForGrouping(normalized.role);
    const timestamp = normalized.timestamp || Date.now();

    if (!currentGroup || currentGroup.role !== role) {
      if (currentGroup) {
        result.push(currentGroup);
      }
      currentGroup = {
        kind: "group",
        key: `group:${role}:${item.key}`,
        role,
        messages: [{ message: item.message, key: item.key }],
        timestamp,
        isStreaming: false,
      };
    } else {
      currentGroup.messages.push({ message: item.message, key: item.key });
    }
  }

  if (currentGroup) {
    result.push(currentGroup);
  }
  return result;
}

function buildChatItems(props: ChatProps): Array<ChatItem | MessageGroup> {
  const items: ChatItem[] = [];
  const history = Array.isArray(props.messages) ? props.messages : [];
  const tools = Array.isArray(props.toolMessages) ? props.toolMessages : [];
  const historyStart = Math.max(0, history.length - CHAT_HISTORY_RENDER_LIMIT);
  if (historyStart > 0) {
    items.push({
      kind: "message",
      key: "chat:history:notice",
      message: {
        role: "system",
        content: `Showing last ${CHAT_HISTORY_RENDER_LIMIT} messages (${historyStart} hidden).`,
        timestamp: Date.now(),
      },
    });
  }
  for (let i = historyStart; i < history.length; i++) {
    const msg = history[i];
    const normalized = normalizeMessage(msg);
    const raw = msg as Record<string, unknown>;
    const marker = raw.__igloobot as Record<string, unknown> | undefined;
    if (marker && marker.kind === "compaction") {
      items.push({
        kind: "divider",
        key:
          typeof marker.id === "string"
            ? `divider:compaction:${marker.id}`
            : `divider:compaction:${normalized.timestamp}:${i}`,
        label: "Compaction",
        timestamp: normalized.timestamp ?? Date.now(),
      });
      continue;
    }

    if (normalized.role.toLowerCase() === "toolresult") {
      if (!props.showThinking) {
        continue;
      }
      // Even in thinking mode the update_plan ack is pure noise — the plan is
      // already rendered as the pinned checklist and the tool-call card.
      if (isPlanAckToolResult(msg)) {
        continue;
      }
    }

    items.push({
      kind: "message",
      key: messageKey(msg, i),
      message: msg,
    });
  }
  if (props.showThinking) {
    for (let i = 0; i < tools.length; i++) {
      if (isPlanAckToolResult(tools[i])) {
        continue;
      }
      items.push({
        kind: "message",
        key: messageKey(tools[i], i + history.length),
        message: tools[i],
      });
    }
  }

  if (props.stream !== null) {
    const key = `stream:${props.sessionKey}:${props.streamStartedAt ?? "live"}`;
    if (props.stream.trim().length > 0) {
      items.push({
        kind: "stream",
        key,
        text: props.stream,
        startedAt: props.streamStartedAt ?? Date.now(),
      });
    } else {
      items.push({ kind: "reading-indicator", key });
    }
  }

  return groupMessages(items);
}

/**
 * True when a toolResult message carries an update_plan ack
 * ({planId,total,done,...}) — suppressed from chat because the pinned
 * checklist and the tool-call card already represent the plan.
 */
function isPlanAckToolResult(message: unknown): boolean {
  const m = message as Record<string, unknown> | null;
  const role = typeof m?.role === "string" ? m.role.toLowerCase() : "";
  if (role !== "toolresult" && role !== "tool_result" && role !== "tool") {
    return false;
  }
  const content = Array.isArray(m?.content) ? (m.content as Array<unknown>) : [];
  for (const item of content) {
    const it = item as Record<string, unknown> | null;
    const text = typeof it?.text === "string" ? it.text : "";
    if (!text.trimStart().startsWith("{")) {
      continue;
    }
    try {
      const parsed = JSON.parse(text) as Record<string, unknown>;
      if (typeof parsed.planId === "string" && typeof parsed.total === "number") {
        return true;
      }
    } catch {
      // not JSON — fall through
    }
  }
  return false;
}

const PLAN_TOOL_NAME = "update_plan";
const PLAN_TOOL_CALL_TYPES = new Set(["toolcall", "tool_call", "tooluse", "tool_use"]);
/** Mirrors MAX_PLAN_STEPS server-side (plan-tool.helpers.ts). */
const PLAN_MAX_STEPS = 50;

type ActivePlan = { title?: string; steps: PlanChecklistStep[] };

/**
 * Extract the raw arguments of every update_plan tool call inside a message's
 * content array, newest-first. Detection mirrors extractToolCards in
 * tool-cards.ts (both the explicit tool-call item types and the
 * name+arguments fallback shape).
 */
function planCallArgs(message: unknown): unknown[] {
  const m = message as Record<string, unknown> | null;
  const content = Array.isArray(m?.content) ? (m.content as Array<unknown>) : [];
  const out: unknown[] = [];
  for (let i = content.length - 1; i >= 0; i--) {
    const item = content[i] as Record<string, unknown> | null;
    if (!item || typeof item !== "object") {
      continue;
    }
    const kind = (typeof item.type === "string" ? item.type : "").toLowerCase();
    if (kind === "toolresult" || kind === "tool_result") {
      continue;
    }
    const args = item.arguments ?? item.args;
    const isToolCall =
      PLAN_TOOL_CALL_TYPES.has(kind) || (typeof item.name === "string" && args != null);
    if (!isToolCall || item.name !== PLAN_TOOL_NAME) {
      continue;
    }
    out.push(args);
  }
  return out;
}

/**
 * Parse update_plan arguments into a plan shape. Arguments may arrive as a
 * JSON string or an object depending on the model/stream — handle both.
 * Returns null when the shape is not a valid full-state plan update.
 */
function parsePlanArgs(raw: unknown): ActivePlan | null {
  let value = raw;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  const record = value as Record<string, unknown>;
  if (!Array.isArray(record.steps)) {
    return null;
  }
  const steps: PlanChecklistStep[] = [];
  // Belt-and-braces: the server rejects >50-step plans, but the client renders
  // straight from call args, so cap here too to keep the render count bounded.
  for (const entry of record.steps.slice(0, PLAN_MAX_STEPS)) {
    const step = entry as Record<string, unknown> | null;
    if (
      !step ||
      typeof step !== "object" ||
      typeof step.text !== "string" ||
      typeof step.status !== "string"
    ) {
      return null;
    }
    steps.push({ text: step.text, status: step.status });
  }
  const title =
    typeof record.title === "string" && record.title.trim() ? record.title.trim() : undefined;
  return { title, steps };
}

/**
 * Only explicitly abandoned plans (steps:[]) disappear from the live view.
 * A completed plan (all done/skipped) stays pinned showing its final all-✓
 * state until a new plan replaces it — users expect to SEE the finished
 * checklist, not have it vanish the moment the task ends.
 */
function finalizeActivePlan(plan: ActivePlan): ActivePlan | null {
  if (!plan.steps.length) {
    return null; // no plan content to show
  }
  return plan;
}

/** Mirror of the server semantics: steps:[] COMPLETES the plan it follows. */
function completeSteps(steps: PlanChecklistStep[]): PlanChecklistStep[] {
  return steps.map((s) => ({
    text: s.text,
    status: s.status === "done" || s.status === "skipped" ? s.status : "done",
  }));
}

/**
 * Derive the agent's active plan from the NEWEST update_plan tool call visible
 * in the chat stream (design D13: client-derived, full-state per call — D5).
 * toolMessages carry the live run's tool calls and render after history, so
 * they are the newest part of the stream. Older calls are consulted to carry
 * the plan title forward (updates may omit it) and to resolve a trailing
 * steps:[] call, which — mirroring the server tool — COMPLETES the previous
 * plan (every unsettled step shown done) rather than hiding the checklist.
 * Note: derived from call ARGUMENTS, not server acks — a server-rejected
 * update may show transiently until the model retries.
 */
function deriveActivePlan(props: ChatProps): ActivePlan | null {
  const sources: unknown[][] = [
    Array.isArray(props.toolMessages) ? props.toolMessages : [],
    Array.isArray(props.messages) ? props.messages : [],
  ];
  let newest: ActivePlan | null = null;
  let completeNewest = false;
  for (const source of sources) {
    for (let i = source.length - 1; i >= 0; i--) {
      for (const raw of planCallArgs(source[i])) {
        const parsed = parsePlanArgs(raw);
        if (!parsed) {
          continue;
        }
        if (!parsed.steps.length) {
          // steps:[] completes the plan carried by the previous call.
          if (!newest) {
            completeNewest = true;
          }
          continue;
        }
        if (!newest) {
          newest = completeNewest ? { ...parsed, steps: completeSteps(parsed.steps) } : parsed;
        } else if (parsed.title) {
          newest.title = parsed.title;
        }
        if (newest.title) {
          return finalizeActivePlan(newest);
        }
      }
    }
  }
  return newest ? finalizeActivePlan(newest) : null;
}

function messageKey(message: unknown, index: number): string {
  const m = message as Record<string, unknown>;
  const toolCallId = typeof m.toolCallId === "string" ? m.toolCallId : "";
  if (toolCallId) {
    return `tool:${toolCallId}`;
  }
  const id = typeof m.id === "string" ? m.id : "";
  if (id) {
    return `msg:${id}`;
  }
  const messageId = typeof m.messageId === "string" ? m.messageId : "";
  if (messageId) {
    return `msg:${messageId}`;
  }
  const timestamp = typeof m.timestamp === "number" ? m.timestamp : null;
  const role = typeof m.role === "string" ? m.role : "unknown";
  if (timestamp != null) {
    return `msg:${role}:${timestamp}:${index}`;
  }
  return `msg:${role}:${index}`;
}
